/**
 * Tenants Page - Enterprise Admin
 * ================================
 * صفحة إدارة الشركات/المستأجرين - نظام شامل
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../../../lib/ui/Card';
import { Table } from '../../../lib/ui/Table';
import { Button } from '../../../lib/ui/Button';
import { SearchBox } from '../../../lib/ui/SearchBox';
import { Modal } from '../../../lib/ui/Modal';
import { Input } from '../../../lib/ui/Input';
import { Dropdown } from '../../../lib/ui/Dropdown';
import { tenantsService } from '../../services/api';
import { useToast } from '../../context/ToastContext';
import SAR from '../../../components/SAR';
import './TenantsPage.css';

// أيقونات
const Icons = {
  building: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 21h18M9 8h1M9 12h1M9 16h1M14 8h1M14 12h1M14 16h1" /><path d="M5 21V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v16" /></svg>,
  users: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" /></svg>,
  orders: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2" /><rect x="9" y="3" width="6" height="4" rx="1" /></svg>,
  revenue: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="1" x2="12" y2="23" /><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" /></svg>,
  filter: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" /></svg>,
  download: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" /></svg>,
  eye: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></svg>,
  edit: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" /></svg>,
  trash: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6" /><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /></svg>,
  check: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="20 6 9 17 4 12" /></svg>,
  x: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>,
};

const TenantsPage = () => {
  const navigate = useNavigate();
  const toast = useToast();

  const [loading, setLoading] = useState(true);
  const [tenants, setTenants] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [planFilter, setPlanFilter] = useState('all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [newTenant, setNewTenant] = useState({ name: '', email: '', phone: '', plan: 'basic' });
  const [saving, setSaving] = useState(false);

  // إحصائيات
  const stats = useMemo(() => {
    const active = tenants.filter(t => t.status === 'active').length;
    const suspended = tenants.filter(t => t.status === 'suspended').length;
    const totalUsers = tenants.reduce((sum, t) => sum + (t.users_count || 0), 0);
    const totalOrders = tenants.reduce((sum, t) => sum + (t.orders_count || 0), 0);
    const totalRevenue = tenants.reduce((sum, t) => sum + (t.revenue || 0), 0);
    return { total: tenants.length, active, suspended, totalUsers, totalOrders, totalRevenue };
  }, [tenants]);

  useEffect(() => {
    loadTenants();
  }, []);

  const loadTenants = async () => {
    setLoading(true);
    try {
      const data = await tenantsService.getAll();
      const list = data.items || data || [];
      // استخدام البيانات الحقيقية من قاعدة البيانات
      const enrichedList = list.map(t => ({
        ...t,
        plan: t.plan_code || t.plan || 'basic',
        users_count: t.users_count || 0,
        customers_count: t.customers_count || 0,
        orders_count: t.orders_count || 0,
        revenue: t.revenue || 0,
        source: 'database'
      }));
      setTenants(enrichedList);
    } catch (error) {
      console.error('Failed to load tenants:', error);
      toast.error('فشل في تحميل بيانات الشركات');
      setTenants([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAddTenant = async () => {
    if (!newTenant.name) {
      toast.error('اسم الشركة مطلوب');
      return;
    }

    setSaving(true);
    try {
      // Generate slug from name
      const slug = newTenant.name
        .toLowerCase()
        .replace(/\s+/g, '-')
        .replace(/[^\w\-أ-ي]/g, '')
        .substring(0, 50) + '-' + Date.now().toString(36);
      
      const tenantData = {
        name: newTenant.name,
        slug: slug,
        status: 'active',
        plan_code: newTenant.plan || 'basic',
      };
      
      await tenantsService.create(tenantData);
      toast.success('تم إضافة الشركة بنجاح');
      setShowAddModal(false);
      setNewTenant({ name: '', email: '', phone: '', plan: 'basic' });
      loadTenants();
    } catch (error) {
      console.error('Failed to create tenant:', error);
      toast.error('فشل إضافة الشركة');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteTenant = async () => {
    if (!selectedTenant) return;
    setSaving(true);
    try {
      await tenantsService.delete(selectedTenant.id);
      toast.success('تم حذف الشركة بنجاح');
      setShowDeleteModal(false);
      setSelectedTenant(null);
      loadTenants();
    } catch (error) {
      toast.error('فشل حذف الشركة');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleStatus = async (tenant) => {
    try {
      if (tenant.status === 'active') {
        await tenantsService.suspend(tenant.id);
        toast.success('تم تعليق الشركة');
      } else {
        await tenantsService.activate(tenant.id);
        toast.success('تم تفعيل الشركة');
      }
      loadTenants();
    } catch (error) {
      toast.error('فشل تحديث حالة الشركة');
    }
  };

  const Money = ({ n }) => <>{new Intl.NumberFormat('ar-SA').format(n || 0)}{' '}<SAR className="h-[0.8em] w-auto" /></>;

  const columns = [
    { key: 'name', title: 'الشركة', render: (val, row) => (
      <div className="watrly-tenants__company-cell">
        <div className="watrly-tenants__company-avatar">
          {val?.charAt(0) || 'ش'}
        </div>
        <div className="watrly-tenants__company-info">
          <span className="watrly-tenants__company-name">{val}</span>
          <span className="watrly-tenants__company-email">{row.contact_email}</span>
        </div>
      </div>
    )},
    { key: 'plan', title: 'الباقة', render: (val) => (
      <span className={`watrly-tenants__plan watrly-tenants__plan--${val}`}>
        {val === 'enterprise' ? 'مؤسسات' : val === 'business' ? 'أعمال' : 'أساسي'}
      </span>
    )},
    { key: 'users_count', title: 'المستخدمين', render: (val) => (
      <span className="watrly-tenants__count">{val || 0}</span>
    )},
    { key: 'customers_count', title: 'العملاء', render: (val) => (
      <span className="watrly-tenants__count">{val || 0}</span>
    )},
    { key: 'orders_count', title: 'الطلبات', render: (val) => (
      <span className="watrly-tenants__count">{val || 0}</span>
    )},
    { key: 'revenue', title: 'الإيرادات', render: (val) => (
      <span className="watrly-tenants__revenue"><Money n={val} /></span>
    )},
    { key: 'status', title: 'الحالة', render: (val) => (
      <span className={`watrly-tenants__status watrly-tenants__status--${val}`}>
        <span className="watrly-tenants__status-dot" />
        {val === 'active' ? 'نشط' : 'معلق'}
      </span>
    )},
    { key: 'actions', title: '', sortable: false, render: (_, row) => (
      <div className="watrly-tenants__actions">
        <button 
          className="watrly-tenants__action-btn watrly-tenants__action-btn--view"
          onClick={() => navigate(`/admin/tenants/${row.id}`)}
          title="عرض التفاصيل"
        >
          {Icons.eye}
        </button>
        <button 
          className="watrly-tenants__action-btn watrly-tenants__action-btn--edit"
          onClick={() => navigate(`/admin/tenants/${row.id}/edit`)}
          title="تعديل"
        >
          {Icons.edit}
        </button>
        <button 
          className={`watrly-tenants__action-btn watrly-tenants__action-btn--toggle ${row.status === 'active' ? 'watrly-tenants__action-btn--suspend' : 'watrly-tenants__action-btn--activate'}`}
          onClick={() => handleToggleStatus(row)}
          title={row.status === 'active' ? 'تعليق' : 'تفعيل'}
        >
          {row.status === 'active' ? Icons.x : Icons.check}
        </button>
        <button 
          className="watrly-tenants__action-btn watrly-tenants__action-btn--delete"
          onClick={() => { setSelectedTenant(row); setShowDeleteModal(true); }}
          title="حذف"
        >
          {Icons.trash}
        </button>
      </div>
    )},
  ];

  const filteredTenants = useMemo(() => {
    return tenants.filter(t => {
      const matchesSearch = t.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        t.contact_email?.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesStatus = statusFilter === 'all' || t.status === statusFilter;
      const matchesPlan = planFilter === 'all' || t.plan === planFilter;
      return matchesSearch && matchesStatus && matchesPlan;
    });
  }, [tenants, searchQuery, statusFilter, planFilter]);

  const planOptions = [
    { value: 'basic', label: 'أساسي' },
    { value: 'business', label: 'أعمال' },
    { value: 'enterprise', label: 'مؤسسات' },
  ];

  const statusOptions = [
    { value: 'all', label: 'جميع الحالات' },
    { value: 'active', label: 'نشط' },
    { value: 'suspended', label: 'معلق' },
  ];

  const planFilterOptions = [
    { value: 'all', label: 'جميع الباقات' },
    ...planOptions,
  ];

  return (
    <div className="watrly-tenants">
      {/* Header */}
      <div className="watrly-tenants__header">
        <div>
          <h1 className="watrly-tenants__title">إدارة الشركات</h1>
          <p className="watrly-tenants__subtitle">
            إدارة المستأجرين والشركات المسجلة في المنصة • {stats.total} شركة
          </p>
        </div>
        <div className="watrly-tenants__header-actions">
          <Button variant="ghost" onClick={() => {}}>
            {Icons.download}
            تصدير
          </Button>
          <Button variant="primary" onClick={() => setShowAddModal(true)}>
            + إضافة شركة
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="watrly-tenants__stats">
        <div className="watrly-tenants__stat-card">
          <div className="watrly-tenants__stat-icon watrly-tenants__stat-icon--primary">
            {Icons.building}
          </div>
          <div className="watrly-tenants__stat-content">
            <span className="watrly-tenants__stat-value">{stats.total}</span>
            <span className="watrly-tenants__stat-label">إجمالي الشركات</span>
          </div>
        </div>
        <div className="watrly-tenants__stat-card">
          <div className="watrly-tenants__stat-icon watrly-tenants__stat-icon--success">
            {Icons.check}
          </div>
          <div className="watrly-tenants__stat-content">
            <span className="watrly-tenants__stat-value">{stats.active}</span>
            <span className="watrly-tenants__stat-label">شركات نشطة</span>
          </div>
        </div>
        <div className="watrly-tenants__stat-card">
          <div className="watrly-tenants__stat-icon watrly-tenants__stat-icon--warning">
            {Icons.users}
          </div>
          <div className="watrly-tenants__stat-content">
            <span className="watrly-tenants__stat-value">{stats.totalUsers}</span>
            <span className="watrly-tenants__stat-label">إجمالي المستخدمين</span>
          </div>
        </div>
        <div className="watrly-tenants__stat-card">
          <div className="watrly-tenants__stat-icon watrly-tenants__stat-icon--info">
            {Icons.revenue}
          </div>
          <div className="watrly-tenants__stat-content">
            <span className="watrly-tenants__stat-value"><Money n={stats.totalRevenue} /></span>
            <span className="watrly-tenants__stat-label">إجمالي الإيرادات</span>
          </div>
        </div>
      </div>

      {/* Search & Filters */}
      <Card padding="md" className="watrly-tenants__filters-card">
        <div className="watrly-tenants__filters">
          <SearchBox
            value={searchQuery}
            onChange={setSearchQuery}
            placeholder="بحث عن شركة بالاسم أو البريد..."
            className="watrly-tenants__search"
          />
          <div className="watrly-tenants__filter-group">
            <Dropdown
              options={statusOptions}
              value={statusFilter}
              onChange={setStatusFilter}
              placeholder="الحالة"
            />
            <Dropdown
              options={planFilterOptions}
              value={planFilter}
              onChange={setPlanFilter}
              placeholder="الباقة"
            />
          </div>
        </div>
        {(statusFilter !== 'all' || planFilter !== 'all' || searchQuery) && (
          <div className="watrly-tenants__active-filters">
            <span>الفلاتر النشطة:</span>
            {searchQuery && (
              <span className="watrly-tenants__filter-tag">
                بحث: {searchQuery}
                <button onClick={() => setSearchQuery('')}>×</button>
              </span>
            )}
            {statusFilter !== 'all' && (
              <span className="watrly-tenants__filter-tag">
                الحالة: {statusFilter === 'active' ? 'نشط' : 'معلق'}
                <button onClick={() => setStatusFilter('all')}>×</button>
              </span>
            )}
            {planFilter !== 'all' && (
              <span className="watrly-tenants__filter-tag">
                الباقة: {planFilter === 'enterprise' ? 'مؤسسات' : planFilter === 'business' ? 'أعمال' : 'أساسي'}
                <button onClick={() => setPlanFilter('all')}>×</button>
              </span>
            )}
            <button 
              className="watrly-tenants__clear-filters"
              onClick={() => { setSearchQuery(''); setStatusFilter('all'); setPlanFilter('all'); }}
            >
              مسح الكل
            </button>
          </div>
        )}
      </Card>

      {/* Results Count */}
      <div className="watrly-tenants__results-info">
        عرض {filteredTenants.length} من {stats.total} شركة
      </div>

      {/* Table */}
      <Card padding="none" className="watrly-tenants__table-card">
        <Table
          columns={columns}
          data={filteredTenants}
          loading={loading}
          emptyMessage="لا توجد شركات مطابقة للبحث"
          hoverable
        />
      </Card>

      {/* Add Modal */}
      <Modal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        title="إضافة شركة جديدة"
        size="md"
        footer={
          <div className="watrly-tenants__modal-actions">
            <Button variant="ghost" onClick={() => setShowAddModal(false)}>
              إلغاء
            </Button>
            <Button variant="primary" onClick={handleAddTenant} loading={saving}>
              إضافة
            </Button>
          </div>
        }
      >
        <div className="watrly-tenants__form">
          <Input
            label="اسم الشركة"
            placeholder="أدخل اسم الشركة"
            value={newTenant.name}
            onChange={(e) => setNewTenant({ ...newTenant, name: e.target.value })}
            required
          />
          <Input
            label="البريد الإلكتروني"
            type="email"
            placeholder="admin@company.com"
            value={newTenant.email}
            onChange={(e) => setNewTenant({ ...newTenant, email: e.target.value })}
          />
          <Input
            label="رقم الهاتف"
            placeholder="05XXXXXXXX"
            value={newTenant.phone}
            onChange={(e) => setNewTenant({ ...newTenant, phone: e.target.value })}
          />
          <Dropdown
            label="الباقة"
            options={planOptions}
            value={newTenant.plan}
            onChange={(val) => setNewTenant({ ...newTenant, plan: val })}
          />
        </div>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => { setShowDeleteModal(false); setSelectedTenant(null); }}
        title="تأكيد الحذف"
        size="sm"
        footer={
          <div className="watrly-tenants__modal-actions">
            <Button variant="ghost" onClick={() => { setShowDeleteModal(false); setSelectedTenant(null); }}>
              إلغاء
            </Button>
            <Button variant="danger" onClick={handleDeleteTenant} loading={saving}>
              حذف
            </Button>
          </div>
        }
      >
        <div className="watrly-tenants__delete-confirm">
          <div className="watrly-tenants__delete-icon">
            {Icons.trash}
          </div>
          <p>هل أنت متأكد من حذف شركة <strong>{selectedTenant?.name}</strong>؟</p>
          <p className="watrly-tenants__delete-warning">هذا الإجراء لا يمكن التراجع عنه وسيتم حذف جميع البيانات المرتبطة.</p>
        </div>
      </Modal>
    </div>
  );
};

export default TenantsPage;
